import { useMemo } from 'react';
import { Plus, Trash2, X } from '@/components/ui/icons';
import type { FilterOperator, FilterRule, Location, ViewSettings } from '@shared/types';
import { PRIORITIES } from '@shared/constants';
import { newId } from '@shared/ids';
import { useDb, type Tables } from '@/lib/store';
import { fieldsForLocation, statusesForLocation } from '@/lib/hierarchy';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { PeopleMenu } from '@/components/fields/People';
import { Avatar } from '@/components/ui/Avatar';
import { StatusIcon } from '@/components/fields/Status';

interface FieldDef {
  id: string;
  label: string;
  kind: 'set' | 'date' | 'text' | 'number' | 'bool';
  options?: { id: string; label: string; color?: string; icon?: React.ReactNode }[];
}

const OPS: Record<FieldDef['kind'], { op: FilterOperator; label: string }[]> = {
  set: [
    { op: 'is', label: 'is' },
    { op: 'is_not', label: 'is not' },
    { op: 'set', label: 'is set' },
    { op: 'not_set', label: 'is not set' },
  ],
  date: [
    { op: 'today', label: 'is today' },
    { op: 'overdue', label: 'is overdue' },
    { op: 'this_week', label: 'is this week' },
    { op: 'next_week', label: 'is next week' },
    { op: 'set', label: 'is set' },
    { op: 'not_set', label: 'is not set' },
  ],
  text: [
    { op: 'contains', label: 'contains' },
    { op: 'set', label: 'is set' },
    { op: 'not_set', label: 'is not set' },
  ],
  number: [
    { op: 'gt', label: '>' },
    { op: 'lt', label: '<' },
    { op: 'is', label: '=' },
    { op: 'set', label: 'is set' },
    { op: 'not_set', label: 'is not set' },
  ],
  bool: [
    { op: 'set', label: 'is checked' },
    { op: 'not_set', label: 'is unchecked' },
  ],
};

function useFieldDefs(db: Tables, location: Location): FieldDef[] {
  return useMemo(() => {
    const statuses = statusesForLocation(db, location);
    const defs: FieldDef[] = [
      { id: 'status', label: 'Status', kind: 'set', options: statuses.map((s) => ({ id: s.name.toLowerCase(), label: s.name, icon: <StatusIcon type={s.type} color={s.color} /> })) },
      { id: 'assignee', label: 'Assignee', kind: 'set' },
      { id: 'priority', label: 'Priority', kind: 'set', options: PRIORITIES.map((p) => ({ id: String(p.value), label: p.label, color: p.color })) },
      { id: 'dueDate', label: 'Due date', kind: 'date' },
      { id: 'startDate', label: 'Start date', kind: 'date' },
      { id: 'dateCreated', label: 'Date created', kind: 'date' },
      { id: 'tags', label: 'Tags', kind: 'set', options: Object.values(db.tags).map((t) => ({ id: t.id, label: t.name, color: t.color })) },
      { id: 'taskType', label: 'Task type', kind: 'set', options: Object.values(db.taskTypes).map((t) => ({ id: t.id, label: t.name })) },
      { id: 'createdBy', label: 'Created by', kind: 'set' },
      { id: 'name', label: 'Task name', kind: 'text' },
    ];
    for (const f of fieldsForLocation(db, location)) {
      const kind: FieldDef['kind'] =
        f.type === 'dropdown' || f.type === 'labels' || f.type === 'people' ? 'set' : f.type === 'date' ? 'date' : f.type === 'checkbox' ? 'bool' : f.type === 'number' || f.type === 'money' || f.type === 'rating' || f.type === 'progress' ? 'number' : 'text';
      defs.push({ id: `cf:${f.id}`, label: f.name, kind, options: f.options?.map((o) => ({ id: o.id, label: o.name, color: o.color })) });
    }
    return defs;
  }, [db, location]);
}

function ValuePicker({ def, rule, onChange }: { def: FieldDef; rule: FilterRule; onChange: (r: FilterRule) => void }) {
  const users = useDb((db) => db.users);
  if (['set', 'not_set', 'today', 'overdue', 'this_week', 'next_week'].includes(rule.operator)) return <span className="filter-value faint">—</span>;
  if (def.kind === 'text' || def.kind === 'number') {
    return (
      <input
        className="input filter-value"
        style={{ height: 28 }}
        type={def.kind === 'number' ? 'number' : 'text'}
        value={String(rule.value ?? '')}
        placeholder="Value"
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
      />
    );
  }
  const selected = (Array.isArray(rule.value) ? rule.value : []) as string[];
  const isPeople = def.id === 'assignee' || def.id === 'createdBy' || (!def.options && def.kind === 'set');
  const label = selected.length
    ? selected
        .map((v) => (isPeople ? (v === 'me' ? 'Me' : (users[v]?.name ?? v)) : (def.options?.find((o) => o.id === v)?.label ?? v)))
        .join(', ')
    : 'Select option';
  return (
    <Popover
      content={() =>
        isPeople ? (
          <PeopleMenu selected={selected} onChange={(ids) => onChange({ ...rule, value: ids })} title={def.label} />
        ) : (
          <Menu style={{ width: 240 }}>
            {(def.options ?? []).map((o) => (
              <MenuItem
                key={o.id}
                icon={o.icon ?? (o.color ? <span className="dot" style={{ background: o.color }} /> : undefined)}
                label={o.label}
                checked={selected.includes(o.id)}
                onClick={() => onChange({ ...rule, value: selected.includes(o.id) ? selected.filter((x) => x !== o.id) : [...selected, o.id] })}
              />
            ))}
          </Menu>
        )
      }
    >
      <button className="filter-select filter-value">
        {isPeople && selected.length > 0 && selected[0] !== 'me' && <Avatar userId={selected[0]} size={16} />}
        <span className="truncate">{label}</span>
      </button>
    </Popover>
  );
}

export function FilterPanel({ location, settings, onChange }: { location: Location; settings: ViewSettings; onChange: (s: Partial<ViewSettings>) => void }) {
  const db = useDb((d) => d);
  const defs = useFieldDefs(db, location);
  const rules = settings.filters.rules;
  const setRules = (r: FilterRule[]) => onChange({ filters: { ...settings.filters, rules: r } });
  const update = (r: FilterRule) => setRules(rules.map((x) => (x.id === r.id ? r : x)));
  const add = () => setRules([...rules, { id: newId(), field: 'status', operator: 'is', value: [] }]);
  return (
    <div className="filter-panel">
      <div className="filter-head">
        <span style={{ fontWeight: 600 }}>Filters</span>
        <span className="spacer" />
        {rules.length > 0 && (
          <button className="btn sm" onClick={() => setRules([])}>
            Clear all
          </button>
        )}
      </div>
      {rules.length === 0 && <div className="faint" style={{ fontSize: 'var(--fs-md)', padding: '6px 2px 10px' }}>No filters applied. Add one to narrow down tasks.</div>}
      {rules.map((r, i) => {
        const def = defs.find((d) => d.id === r.field) ?? defs[0];
        return (
          <div key={r.id} className="filter-row">
            <span className="filter-conj">
              {i === 0 ? (
                'Where'
              ) : i === 1 ? (
                <button className="filter-select" onClick={() => onChange({ filters: { ...settings.filters, op: settings.filters.op === 'and' ? 'or' : 'and' } })}>
                  {settings.filters.op === 'and' ? 'AND' : 'OR'}
                </button>
              ) : settings.filters.op === 'and' ? (
                'AND'
              ) : (
                'OR'
              )}
            </span>
            <Popover
              content={(close) => (
                <Menu style={{ width: 220, maxHeight: 340 }}>
                  {defs.map((d) => (
                    <MenuItem key={d.id} label={d.label} selected={d.id === r.field} onClick={() => (close(), update({ ...r, field: d.id, operator: OPS[d.kind][0].op, value: undefined }))} />
                  ))}
                </Menu>
              )}
            >
              <button className="filter-select">
                <span className="truncate">{def.label}</span>
              </button>
            </Popover>
            <Popover
              content={(close) => (
                <Menu>
                  {OPS[def.kind].map((o) => (
                    <MenuItem key={o.op} label={o.label} selected={o.op === r.operator} onClick={() => (close(), update({ ...r, operator: o.op }))} />
                  ))}
                </Menu>
              )}
            >
              <button className="filter-select">{OPS[def.kind].find((o) => o.op === r.operator)?.label ?? r.operator}</button>
            </Popover>
            <ValuePicker def={def} rule={r} onChange={update} />
            <button className="icon-btn sm" onClick={() => setRules(rules.filter((x) => x.id !== r.id))}>
              <Trash2 size={14} />
            </button>
          </div>
        );
      })}
      <button className="btn sm" onClick={add} style={{ marginTop: 6 }}>
        <Plus size={14} /> Add filter
      </button>
    </div>
  );
}

export function ActiveFilterCount({ settings, onClear }: { settings: ViewSettings; onClear: () => void }) {
  const n = settings.filters.rules.length;
  if (!n) return null;
  return (
    <span className="chip accent">
      {n} filter{n > 1 ? 's' : ''}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
      >
        <X size={12} />
      </button>
    </span>
  );
}
